import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { RegionState, SkeletonLoader } from '@freelancelabs/components';

import { AccountSocietiesDocument } from '../../graphql/__generated__/types';
import AccountSocietyPanel from './AccountSocietyPanel';
import { accountsRefusalOf } from './accountsRefusal';

interface Props {
    /** Recherche déjà débouncée. */
    debouncedSearch: string;
    portalEnabled: boolean | null;
    moduleTotal: number | null;
}

/** Un `includes` insensible à la casse et aux accents, pour la recherche client. */
const matches = (haystack: string | null | undefined, needle: string) =>
    (haystack ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .includes(needle);

/**
 * La vue GROUPÉE de la page « Comptes » (PCG-333) : les sociétés mères en index, puis
 * les comptes indépendants à plat.
 *
 * `accountSocieties` est un ensemble COMPLET (non paginé), donc la recherche et le
 * filtre portail s'y appliquent côté client : la règle du dépôt interdit de filtrer une
 * PAGE, pas un ensemble déjà entier. Précédent du filtre « Marché » de « Mon activité ».
 *
 * Attention, le filtre portail masque une société sans compte de l'état demandé, mais
 * ne filtre PAS son dépliage : le compteur de l'en-tête est le total du périmètre
 * (`accountCount`), et `accounts(rootId:)` doit lui correspondre. Le « M ouverts » de
 * l'en-tête dit déjà la répartition. La recherche, elle, cible les sociétés par nom ou
 * SIREN ici, et les comptes indépendants côté serveur : elle ne descend pas dans le
 * dépliage d'une société, dont le compteur mentirait alors.
 */
const GroupedAccounts = ({ debouncedSearch, portalEnabled, moduleTotal }: Props) => {
    const [openId, setOpenId] = useState<string | null>(null);

    const { data, loading, error } = useQuery(AccountSocietiesDocument, { fetchPolicy: 'cache-and-network' });

    const needle = debouncedSearch
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();

    const societies = (data?.accountSocieties ?? []).filter((society) => {
        if (needle && !matches(society.rootOrganizationName, needle) && !matches(society.siren, needle)) {
            return false;
        }
        // Une société sans compte de l'état demandé sort de l'index. Nul n'est pas
        // zéro : une société dont l'API n'a pas dit le compte reste visible.
        if (portalEnabled === true) {
            return society.portalEnabledCount === null || society.portalEnabledCount > 0;
        }
        if (portalEnabled === false) {
            if (society.accountCount === null || society.portalEnabledCount === null) {
                return true;
            }
            return society.accountCount - society.portalEnabledCount > 0;
        }
        return true;
    });

    const refusal = error ? accountsRefusalOf(error) : null;
    const filtered = Boolean(debouncedSearch) || portalEnabled !== null;

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-1">
                {refusal ? (
                    <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
                ) : loading && !data ? (
                    // Le skeleton du design system plutôt qu'un texte, comme le front : l'attente
                    // prend la forme de ce qui vient, une colonne d'en-têtes de sociétés repliés.
                    // Hauteur et rayon repris de la carte `--white-head` (56 px d'en-tête).
                    <div aria-label={c('Account societies').t`Loading the parent companies…`} aria-busy="true">
                        {[0, 1, 2, 3].map((index) => (
                            <SkeletonLoader
                                key={index}
                                height={56}
                                className="mb-2 rounded-[var(--portal-section-radius)]"
                            />
                        ))}
                    </div>
                ) : societies.length === 0 ? (
                    <RegionState
                        title={
                            filtered
                                ? c('Account societies').t`No parent company matches these criteria.`
                                : c('Account societies').t`No parent company in your portfolio.`
                        }
                        description={
                            filtered
                                ? c('Account societies')
                                      .t`The search reads the company name and its SIREN. A single-account client is not a company here: look for it in the flat accounts below.`
                                : c('Account societies')
                                      .t`A parent company groups several accounts of one client. Accounts that stand on their own are listed below.`
                        }
                    />
                ) : (
                    societies.map((society) => (
                        <AccountSocietyPanel
                            key={society.rootOrganizationId}
                            society={society}
                            open={openId === society.rootOrganizationId}
                            onOpenChange={(open) => setOpenId(open ? society.rootOrganizationId : null)}
                            moduleTotal={moduleTotal}
                        />
                    ))
                )}
            </section>
        </div>
    );
};

export default GroupedAccounts;
